import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
  GridSortItem,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { memo, useCallback, useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useLink } from '~/hooks/link';
import { useLocation } from '~/hooks/location';
import UploadMediaIllustration from '~/images/illustrations/upload-media.svg';
import { useColumns } from '../lib';
import type { ContentCardProps } from './ContentCard';
import type { ContentFolderCardProps } from './ContentFolderCard';

const NoRowsOverlay = () => {
  const { t } = useTranslation();

  return (
    <EmptyState
      illustration={UploadMediaIllustration}
      header={t('pages.content.empty_state.header')}
      description={t('pages.content.empty_state.description')}
    />
  );
};

export interface ContentTableProps {
  items: readonly ContentCardProps['item'][];
  folders: readonly ContentFolderCardProps['contentFolder'][];
  loading: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  onSortModelChange?: (sortModel: GridSortItem | null) => void;
  search?: boolean;
  selectedIds: GridRowSelectionModel;
  sortModel?: GridSortModel;
}

type ContentOrFolder = ContentCardProps['item'] | ContentFolderCardProps['contentFolder'];

export const ContentTable = memo(
  ({
    items,
    folders,
    loading,
    onCheck,
    onSortModelChange,
    search = false,
    selectedIds,
    sortModel,
  }: ContentTableProps) => {
    const location = useLocation();
    const link = useLink();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    searchParams.set('page', '1');
    const combinedData = [...folders, ...items];

    const handleSortModelChange = useCallback(
      (model: GridSortModel) => {
        onSortModelChange?.(model.at(0) ?? null);
      },
      [onSortModelChange],
    );

    const columns = useColumns({ search });
    const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

    const theme = useTheme();
    const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumAndDown = useMediaQuery(theme.breakpoints.down('md'));
    const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));

    useEffect(() => {
      setShowColumns((x) => ({
        ...x,
        thumbnail: !isSmallAndDown,
        kind: !isMediumAndDown,
        itemCount: !search && !isMediumAndDown,
        originalMetadata: !isLargeAndDown,
        createdAt: !isLargeAndDown,
        location: !isLargeAndDown,
      }));
    }, [isSmallAndDown, isMediumAndDown, isLargeAndDown, search]);

    return (
      <ScrollableDataGrid
        checkboxSelection={!isSmallAndDown}
        columns={columns}
        columnVisibilityModel={showColumns}
        disableRowSelectionOnClick
        hideFooter
        isRowSelectable={({ row }) => row.__typename !== 'ContentFolder'}
        loading={loading}
        onColumnVisibilityModelChange={setShowColumns}
        onRowSelectionModelChange={onCheck}
        onRowClick={({ row }: GridRowParams<ContentOrFolder>) => {
          if (row.__typename === 'ContentFolder') {
            navigate({ pathname: `../${row.id}`, search: searchParams.toString() });
            return;
          }
          if (row.__typename === 'ContentItem' && row.design?.id) {
            return navigate(link(`/studio/${row.design.designFolderId}/design/${row.design.id}`), {
              state: { returnTo: location.fullPath },
            });
          }
          const contentFolderId =
            row.__typename === 'ContentItem' ? row.nullableContentFolderId : row.contentFolderId;
          const itemType = row.__typename === 'ContentItem' ? 'apps' : 'media';
          const defaultTab = row.__typename === 'ContentItem' ? 'settings' : 'details';
          navigate(link(`/content/${contentFolderId}/${itemType}/${row.id}/${defaultTab}`), {
            state: { returnTo: location.fullPath },
          });
        }}
        getRowId={(row) => `${row.__typename}-${row.id}`}
        rows={combinedData}
        rowSelectionModel={selectedIds}
        slots={{
          baseCheckbox: ListCheckbox,
          columnResizeIcon: () => null,
          noRowsOverlay: NoRowsOverlay,
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
      />
    );
  },
);

ContentTable.displayName = 'ContentTable';
